import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormHelperText,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FolderIcon from '@mui/icons-material/Folder';

import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';

const ActionsAddCsvColumn = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();

  const [sourcePath, setSourcePath] = useState('');
  const [targetPath, setTargetPath] = useState('/{filename}_with_column.csv');
  const [columnName, setColumnName] = useState('');
  const [columnValue, setColumnValue] = useState('');
  const [sourceBrowsingDisabled, setSourceBrowsingDisabled] = useState(false);
  const [targetBrowsingDisabled, setTargetBrowsingDisabled] = useState(false);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'addcsvcolumn',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial = {}) => {
      return (
        sourcePath === (initial.sourceFilePath || '') &&
        targetPath === (initial.target || '') &&
        columnName === (initial.columnName || '') &&
        columnValue === (initial.columnValue || '')
      );
    },
    customSaveData: () => ({
      sourceFilePath: sourcePath,
      target: targetPath,
      columnName,
      columnValue
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setSourcePath(customData.sourceFilePath || '');
      setTargetPath(customData.target || '/{filename}_with_column.csv');
      setColumnName(customData.columnName || '');
      setColumnValue(customData.columnValue || '');
    }
  });

  const { handleBrowseDirectory: handleBrowseSourceFile, FolderBrowserComponent: SourceFileBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowsingDisabled(false);
    }
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        const folderPathClean = folderPath.replace(/[\\/]+$/, '');
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        setTargetPath(`${folderPathClean}${separator}{filename}_with_column.csv`);
      }
      setTargetBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowsingDisabled(false);
    }
  });

  useEffect(() => {
    updateSaveDisabledState();
  }, [sourcePath, targetPath, columnName, columnValue, updateSaveDisabledState]);

  const validation = useMemo(() => {
    if (!columnName.trim()) {
      return {
        error: true,
        helper: t('workflow.add_csv_column.column_required', 'Column name is required')
      };
    }
    if (!targetPath.trim()) {
      return {
        error: true,
        helper: t('workflow.add_csv_column.target_required', 'Target path is required')
      };
    }
    return { error: false, helper: '' };
  }, [columnName, targetPath, t]);

  const customDataTags = [
    {
      key: 'filename',
      label: 'Filename',
      value: 'filename',
      description: 'Source filename without extension'
    },
    {
      key: 'file_extension',
      label: 'File Extension',
      value: 'file_extension',
      description: 'Source file extension without the dot'
    },
    {
      key: 'file_folder_path',
      label: 'File Folder Path',
      value: 'file_folder_path',
      description: 'Folder path of the current source file'
    }
  ];

  return (
    <ActionSettingsLayout
      title={t('addcsvcolumn', 'Add CSV Column')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled || validation.error}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      customDataTags={customDataTags}
    >
      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={t('workflow.add_csv_column.source_title', 'Source CSV')}
            titleTypographyProps={{
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 }
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('workflow.add_csv_column.source_label', 'Source file path')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    value={sourcePath}
                    onChange={setSourcePath}
                    placeholder={t('workflow.add_csv_column.source_placeholder', 'Leave blank to use the current event CSV')}
                    singleLine
                    tags={tags}
                  />
                  <FormHelperText>
                    {t('workflow.add_csv_column.source_helper', 'If blank, the current event source file is used.')}
                  </FormHelperText>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSourceBrowsingDisabled(true);
                    handleBrowseSourceFile();
                  }}
                  disabled={sourceBrowsingDisabled}
                  sx={{ mt: 3 }}
                >
                  <FolderIcon sx={{ mr: 1 }} />
                  {t('browse', 'Browse')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={t('workflow.add_csv_column.target_title', 'Target CSV')}
            titleTypographyProps={{
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 }
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('workflow.add_csv_column.target_label', 'Target path')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    value={targetPath}
                    onChange={setTargetPath}
                    placeholder={t('workflow.add_csv_column.target_placeholder', '/{filename}_with_column.csv')}
                    singleLine
                    tags={tags}
                  />
                  <FormHelperText>
                    {t('workflow.add_csv_column.target_helper', 'Choose where the updated CSV should be written.')}
                  </FormHelperText>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTargetBrowsingDisabled(true);
                    handleBrowseTargetDirectory();
                  }}
                  disabled={targetBrowsingDisabled}
                  sx={{ mt: 3 }}
                >
                  <FolderIcon sx={{ mr: 1 }} />
                  {t('browse', 'Browse')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={t('workflow.add_csv_column.column_title', 'New column')}
            titleTypographyProps={{
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 }
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_csv_column.column_name_label', 'Column name')}
                </Typography>
                <TagEditor
                  caseid={caseid}
                  value={columnName}
                  onChange={setColumnName}
                  placeholder={t('workflow.add_csv_column.column_name_placeholder', 'status')}
                  singleLine
                  tags={tags}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_csv_column.column_value_label', 'Column value')}
                </Typography>
                <TagEditor
                  caseid={caseid}
                  value={columnValue}
                  onChange={setColumnValue}
                  placeholder={t('workflow.add_csv_column.column_value_placeholder', 'processed')}
                  minRows={4}
                  multiline
                  tags={tags}
                />
                <FormHelperText error={validation.error}>
                  {validation.error ? validation.helper : t(
                    'workflow.add_csv_column.column_helper',
                    'The value is written in the new column for each row. Use tags if you want dynamic content.'
                  )}
                </FormHelperText>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {SourceFileBrowserComponent}
      {TargetFolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default ActionsAddCsvColumn;
